import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ComposedChart, Line } from "recharts";
import { Eye, Users, Target } from "lucide-react";

const PERIODS = [["weekly", "Weekly"], ["monthly", "Monthly"], ["yearly", "Yearly"]];
const META = {
  weekly: { title: "Last 12 weeks", col: "Week", sub: "last 12 weeks" },
  monthly: { title: "Last 12 months", col: "Month", sub: "last 12 months" },
  yearly: { title: "Last 5 years", col: "Year", sub: "last 5 years" },
};
const num = (n) => Number(n || 0).toLocaleString("en-GB");

export function TrafficPanel() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((p) => {
    setLoading(true);
    api.get(`/admin/analytics/traffic?period=${p}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load site traffic"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(period); }, [period, load]);

  const meta = META[period];

  return (
    <div data-testid="traffic-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Site traffic</h2>
          <p className="text-xs text-[#6B7280]">Visitors &amp; page views for the public website.</p>
        </div>
        <div className="inline-flex border border-[#E5E7EB] bg-white" data-testid="traffic-period-toggle">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`traffic-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${period === v ? "bg-[#12263A] text-white" : "text-[#6B7280] hover:text-[#12263A]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-sm text-[#6B7280]" data-testid="traffic-loading">Loading traffic…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="traffic-kpis">
            <div className="border border-[#E5E7EB] bg-white p-4">
              <div className="flex items-center gap-2 text-[#6B7280]"><Eye size={15} className="text-[#C79A3B]" /><span className="label-eyebrow">Page views</span></div>
              <div className="text-3xl font-black mt-2" data-testid="traffic-total-views">{num(data.totals.views)}</div>
              <div className="text-[11px] text-[#6B7280] mt-1">{meta.sub}</div>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4">
              <div className="flex items-center gap-2 text-[#6B7280]"><Users size={15} className="text-[#C79A3B]" /><span className="label-eyebrow">Unique visitors</span></div>
              <div className="text-3xl font-black mt-2" data-testid="traffic-total-visitors">{num(data.totals.visitors)}</div>
              <div className="text-[11px] text-[#6B7280] mt-1">distinct sessions · {meta.sub}</div>
            </div>
            <div className="border border-[#C79A3B] bg-[#FBF3EC] p-4">
              <div className="flex items-center gap-2 text-[#6B7280]"><Target size={15} className="text-[#C79A3B]" /><span className="label-eyebrow">Enquiries (leads)</span></div>
              <div className="text-3xl font-black mt-2" data-testid="traffic-total-leads">{num(data.totals.leads)}</div>
              <div className="text-[11px] text-[#6B7280] mt-1" data-testid="traffic-conversion">
                {data.totals.conversion != null ? `${data.totals.conversion}% of visitors became enquiries` : "no visitor data yet"}
              </div>
            </div>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-5" data-testid="traffic-chart">
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4">{meta.title}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={data.buckets} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#C79A3B" }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="views" name="Page views" fill="#C79A3B" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="left" dataKey="visitors" name="Visitors" fill="#D9C7B6" radius={[3, 3, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="leads" name="Enquiries" stroke="#12263A" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest mb-3">Breakdown</h3>
              <div className="overflow-x-auto border border-[#E5E7EB] bg-white" data-testid="traffic-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F4] text-left"><tr>{[meta.col, "Views", "Visitors", "Enquiries"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.buckets.map(b => (
                      <tr key={b.key} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2">{b.label}</td>
                        <td className="px-3 py-2 font-semibold">{num(b.views)}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{num(b.visitors)}</td>
                        <td className="px-3 py-2 font-semibold text-[#C79A3B]">{num(b.leads)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest mb-3">Top pages</h3>
              <div className="overflow-x-auto border border-[#E5E7EB] bg-white" data-testid="traffic-top-pages">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F4F4] text-left"><tr>{["Page", "Views"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.top_pages.map(p => (
                      <tr key={p.path} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2 font-mono text-[#12263A]">{p.path}</td>
                        <td className="px-3 py-2 font-semibold">{num(p.views)}</td>
                      </tr>
                    ))}
                    {data.top_pages.length === 0 && <tr><td colSpan={2} className="p-6 text-center text-[#6B7280]">No traffic recorded in this period yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
